import React, { MouseEvent, useRef } from 'react';
import { Box, Typography, Container, Grid, Paper } from '@mui/material';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import {
  Target,
  ShieldCheck,
  Microscope,
  Scale,
  Handshake,
  Recycle,
  Zap
} from 'lucide-react';
import { gradients } from '../theme';
import FadeIn from './FadeIn';

interface Reason {
  title: string;
  description: string;
  icon: React.ElementType;
  span: { xs: number; md: number };
  color: string;
}

const reasons: Reason[] = [
  {
    title: 'Precision Focused',
    description: '100% focused on catalytic converter & precious-metal recovery with specialized industrial expertise.',
    icon: Target,
    span: { xs: 12, md: 8 },
    color: '#00f2ff',
  },
  {
    title: 'Local & Compliant',
    description: 'UAE-based, regulation-compliant operations ensuring full legal peace of mind.',
    icon: ShieldCheck,
    span: { xs: 12, md: 4 },
    color: '#43AB32',
  },
  {
    title: 'Advanced Testing',
    description: 'State-of-the-art PGM analysis using laboratory-grade XRF technology.',
    icon: Microscope,
    span: { xs: 12, md: 3 },
    color: '#1A65C9',
  },
  {
    title: 'Fair & Transparent Payouts',
    description: 'A completely transparent process with fair, market-linked pricing guaranteed for all your materials.',
    icon: Scale,
    span: { xs: 12, md: 3 },
    color: '#00f2ff',
  },
  {
    title: 'Industry Trusted',
    description: 'The preferred partner for professional workshops and scrap dealers.',
    icon: Handshake,
    span: { xs: 12, md: 3 },
    color: '#43AB32',
  },
  {
    title: 'Circular Economy',
    description: 'Driving sustainability through ethical recovery of precious metals.',
    icon: Recycle,
    span: { xs: 12, md: 3 },
    color: '#1A65C9',
  },
];




const BentoCard: React.FC<{ reason: Reason; index: number }> = ({ reason, index }) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const handleMouseMove = (e: MouseEvent) => {
    const { currentTarget, clientX, clientY } = e;
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  };

  const Icon = reason.icon;

  return (
    <Grid size={reason.span} sx={{ display: 'flex' }}>
      <FadeIn delay={index * 0.1} fullWidth>
        <Paper
          component={motion.div}
          onMouseMove={handleMouseMove}
          whileHover={{ y: -5 }}
          sx={{
            position: 'relative',
            p: { xs: 3, md: 3.5 },
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 5,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            transition: 'border-color 0.3s ease',
            '&:hover': {
              borderColor: 'rgba(0, 242, 255, 0.3)',
              '& .icon-wrapper': {
                transform: 'scale(1.1) rotate(5deg)',
                color: '#00f2ff',
              }
            },
          }}
        >
          {/* Spotlight Effect */}
          <Box
            component={motion.div}
            sx={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(400px circle at var(--x) var(--y), rgba(0, 242, 255, 0.06), transparent 40%)`,
              opacity: 0,
              transition: 'opacity 0.3s',
              '&:hover': { opacity: 1 },
            }}
            style={{
              // @ts-ignore
              '--x': `${mouseX.get()}px`,
              // @ts-ignore
              '--y': `${mouseY.get()}px`,
            }}
          />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box
              className="icon-wrapper"
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2.5,
                color: 'grey.400',
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
              }}
            >
              <Icon size={24} />
            </Box>

            <Typography
              variant="h5"
              sx={{
                color: 'white',
                fontWeight: 700,
                mb: 1,
                fontSize: { xs: '1.15rem', md: '1.35rem' },
                letterSpacing: '-0.01em',
              }}
            >
              {reason.title}
            </Typography>
            <Typography
              sx={{
                color: 'grey.400',
                fontSize: { xs: '0.9rem', md: '0.95rem' },
                lineHeight: 1.5,
                maxWidth: 380,
              }}
            >
              {reason.description}
            </Typography>
          </Box>

          <Box
            sx={{
              mt: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 2,
                backgroundColor: reason.color,
                boxShadow: `0 0 10px ${reason.color}`,
                opacity: 0.6,
                borderRadius: 1,
              }}
            />
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: 'rgba(255, 255, 255, 0.2)',
                letterSpacing: 1,
                fontFamily: 'monospace'
              }}
            >
              {String(index + 1).padStart(2, '0')}
            </Typography>
          </Box>
        </Paper>
      </FadeIn>
    </Grid>
  );
};


const WhyChoose: React.FC = () => {
  return (
    <Box
      sx={{
        backgroundColor: 'black',
        py: { xs: 10, md: 12 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Decor */}
      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          right: '-5%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(0, 242, 255, 0.06) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(100px)',
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <FadeIn direction="down">
          <Box sx={{ textAlign: 'left', mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="overline"
              sx={{
                color: '#00f2ff',
                fontWeight: 700,
                letterSpacing: 3,
                mb: 1.5,
                display: 'block',
                textTransform: 'uppercase',
                fontSize: '0.75rem'
              }}
            >
              The Bluecat Advantage
            </Typography>
            <Grid container spacing={4} alignItems="center">
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography
                  variant="h2"
                  sx={{
                    ...gradients.primaryText,
                    fontWeight: 800,
                    fontSize: { xs: '2.5rem', md: '3.8rem' },
                    lineHeight: 1.1,
                    letterSpacing: '-0.03em',
                  }}
                >
                  Setting the standard for metal recovery.
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography
                  sx={{
                    color: 'grey.400',
                    fontSize: { xs: 16, md: 18 },
                    lineHeight: 1.6,
                    borderLeft: '2px solid rgba(0, 242, 255, 0.3)',
                    pl: 3,
                    opacity: 0.8,
                  }}
                >
                  Deep industry roots with cutting-edge analytical technology to maximize value for our partners and environment.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </FadeIn>

        <Grid container spacing={2.5}>
          {reasons.map((reason, index) => (
            <BentoCard key={index} reason={reason} index={index} />
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default WhyChoose;


